import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Form, Input, Select, DatePicker, Button, notification } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { motion } from "motion/react";
import dayjs from "dayjs";
import Contact from "./Contact";

const { TextArea } = Input;
const ease = [0.22, 1, 0.36, 1];

const WHATSAPP_NUMBER = "919028156566";

const treatmentOptions = [
  "General Consultation",
  "Wellness Assessment",
  "Ayurvedic Consultation",
  "Panchakarma Therapy",
  "Arthritis Treatment",
  "Stress & Anxiety",
  "Other",
];

export default function Appointment() {
  const location = useLocation();
  const selectedTreatment = location.state?.treatment;

  const [form] = Form.useForm();
  const [isLocked, setIsLocked] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (selectedTreatment) {
      form.setFieldsValue({ treatment: selectedTreatment });
      setIsLocked(true);
    }
  }, [selectedTreatment, form]);

  const onFinish = (values) => {
    const { fullName, phone, treatment, date, query } = values;

    const formattedDate = date ? dayjs(date).format("DD MMM YYYY") : "N/A";

    const text = `Hello VedaNova, I like to book an appointment.%0A
*Name:* ${fullName}%0A
*Phone:* ${phone}%0A
*Treatment:* ${treatment}%0A
*Preferred Date:* ${formattedDate}%0A
*Query/Message:* ${query || "N/A"}`;

    const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    window.open(whatsappURL, "_blank");

    api.open({
      message: "Appointment Requested",
      description: `Your booking for ${treatment} has been received!`,
      icon: <CheckCircleOutlined className="!text-green-600" />,
      placement: "top",
      duration: 4,
    });
    form.resetFields();
    setIsLocked(false);
  };

  const disabledDate = (current) => current && current < dayjs().startOf("day");

  return (
    <section className="py-24 bg-[#faf6ee] border-b border-brand-lightgold overflow-hidden relative">
      {contextHolder}
       <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="mb-16 text-center max-w-2xl mx-auto"
        >
          <h2 className="uppercase md:text-xl text-base font-extrabold text-brand-gold mb-6">
            Contact Us
          </h2>
          <h3 className="text-4xl font-bold text-brand-forest mb-4">Start Your Path to Wellness Today</h3>
          <p className="text-gray-600">
           Every healing journey begins with a conversation. Tell us about your concerns, and our Ayurvedic experts will help you find the right treatment
          </p>
        </motion.div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="bg-white border border-brand-lightgold rounded-2xl overflow-hidden shadow-xl p-8 lg:p-12"
        >
          <div className="text-center mb-10">
          <p className="text-xl text-brand-forest font-bold">Let's Talk About Your Wellness</p>
            <p className="text-gray-500 text-lg">Schedule your consultation with our experts today.</p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ treatment: selectedTreatment || "General Consultation" }}
            requiredMark={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Form.Item
                label={
                  <span className="text-[11px] font-bold uppercase tracking-widest text-brand-forest">Full Name</span>
                }
                name="fullName"
                rules={[{ required: true, message: "Full Name is required" }]}
              >
                <Input size="large" placeholder="John Doe" className="!bg-brand-beige/50" />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[11px] font-bold uppercase tracking-widest text-brand-forest">Phone Number</span>
                }
                name="phone"
                rules={[
                  { required: true, message: "Phone Number is required" },
                  { pattern: /^[\d\s()+-]{7,20}$/, message: "Please enter a valid phone number" },
                ]}
              >
                <Input size="large" placeholder="+91 98765 43210" className="!bg-brand-beige/50" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Form.Item
                label={
                  <span className="text-[11px] font-bold uppercase tracking-widest text-brand-forest">Treatment Type</span>
                }
                name="treatment"
              >
                <Select
                  size="large"
                  disabled={isLocked}
                  options={treatmentOptions.map((t) => ({ label: t, value: t }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-[11px] font-bold uppercase tracking-widest text-brand-forest">Preferred Date</span>
                }
                name="date"
                rules={[{ required: true, message: "Preferred Date is required" }]}
              >
                <DatePicker size="large" className="!w-full !bg-brand-beige/50" disabledDate={disabledDate} />
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className="text-[11px] font-bold uppercase tracking-widest text-brand-forest">Query/Message</span>
              }
              name="query"
            >
              <TextArea rows={4} placeholder="Tell us about your concerns..." className="!bg-brand-beige/50" />
            </Form.Item>

            {isLocked && (
              <Button type="link" onClick={() => setIsLocked(false)} className="!text-brand-gold !font-semibold !p-0 !mb-4">
                Change treatment
              </Button>
            )}

            <Form.Item>
              <Button
                htmlType="submit"
                block
                size="large"
                className="!bg-brand-forest !text-white !border-none !py-6 !text-[12px] !font-bold !uppercase !tracking-widest !shadow-lg hover:!bg-brand-gold"
              >
                Confirm Booking
              </Button>
            </Form.Item>
          </Form>
        </motion.div>
      </div>
      <Contact/>
    </section>
  );
}